/**
 * Recipes Filters Component
 *
 * Filter bar for the recipes list page.
 */

import { Search, X, Calculator, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui';
import { Select } from '@/components/ui';
import { SearchAutocomplete, type SearchAutocompleteOption } from '@/components/ui';
import { useDebounce } from '@/hooks';
import { useRawProductsSearch, usePosProductsSearch } from '../hooks';

interface RecipesFiltersProps {
  search: string;
  onSearchChange: (value: string) => void;
  hasProduct: 'all' | 'with' | 'without';
  onHasProductChange: (value: 'all' | 'with' | 'without') => void;
  // Raw product filter
  rawProductSearch: string;
  onRawProductSearchChange: (value: string) => void;
  selectedRawProduct: SearchAutocompleteOption | null;
  onRawProductChange: (option: SearchAutocompleteOption | null) => void;
  // POS product filter
  posProductSearch: string;
  onPosProductSearchChange: (value: string) => void;
  selectedPosProduct: SearchAutocompleteOption | null;
  onPosProductChange: (option: SearchAutocompleteOption | null) => void;
  onReset: () => void;
  hasActiveFilters: boolean;
  // Recalculate costs
  onRecalculateAllCosts: () => void;
  isRecalculating?: boolean;
  showFinancials?: boolean;
}

export function RecipesFilters({
  search,
  onSearchChange,
  hasProduct,
  onHasProductChange,
  rawProductSearch,
  onRawProductSearchChange,
  selectedRawProduct,
  onRawProductChange,
  posProductSearch,
  onPosProductSearchChange,
  selectedPosProduct,
  onPosProductChange,
  onReset,
  hasActiveFilters,
  onRecalculateAllCosts,
  isRecalculating = false,
  showFinancials = false,
}: RecipesFiltersProps) {
  // Debounce search values for autocomplete
  const debouncedRawProductSearch = useDebounce(rawProductSearch, 300);
  const debouncedPosProductSearch = useDebounce(posProductSearch, 300);

  // Search hooks
  const { data: rawProductOptions = [], isLoading: isRawProductsLoading } = useRawProductsSearch(
    debouncedRawProductSearch,
    !selectedRawProduct
  );
  const { data: posProductOptions = [], isLoading: isPosProductsLoading } = usePosProductsSearch(
    debouncedPosProductSearch,
    !selectedPosProduct
  );

  return (
    <div className="p-4 bg-white rounded-lg border border-neutral-200">
      {/* All filters on one line - flex-wrap for responsive */}
      <div className="flex flex-wrap items-end gap-4">
        {/* Search */}
        <div className="relative w-[280px] flex-shrink-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <input
            type="text"
            placeholder="Rechercher une recette..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-neutral-200 rounded-lg text-sm
                       focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
                       placeholder:text-neutral-400"
          />
        </div>

        {/* Has Product Filter */}
        <div className="w-[220px] flex-shrink-0">
          <Select
            value={hasProduct}
            onChange={(value) => onHasProductChange(value as 'all' | 'with' | 'without')}
            options={[
              { value: 'all', label: 'Toutes les recettes' },
              { value: 'with', label: 'Avec produit fini' },
              { value: 'without', label: 'Sans produit (orphelines)' },
            ]}
            allowEmpty={false}
          />
        </div>

        {/* Raw Product Filter */}
        <div className="w-[240px] flex-shrink-0">
          <SearchAutocomplete
            placeholder="Filtrer par produit brut..."
            value={selectedRawProduct}
            onChange={onRawProductChange}
            options={rawProductOptions}
            isLoading={isRawProductsLoading}
            searchValue={rawProductSearch}
            onSearchChange={onRawProductSearchChange}
            emptyMessage="Aucun produit brut trouve"
          />
        </div>

        {/* POS Product Filter */}
        <div className="w-[240px] flex-shrink-0">
          <SearchAutocomplete
            placeholder="Filtrer par produit fini POS..."
            value={selectedPosProduct}
            onChange={onPosProductChange}
            options={posProductOptions}
            isLoading={isPosProductsLoading}
            searchValue={posProductSearch}
            onSearchChange={onPosProductSearchChange}
            emptyMessage="Aucun produit POS trouve"
          />
        </div>

        {/* Spacer to push buttons to the right */}
        <div className="flex-grow" />

        {/* Recalculate Costs Button - only for managers+ */}
        {showFinancials && (
          <Button
            variant="secondary"
            size="sm"
            onClick={onRecalculateAllCosts}
            disabled={isRecalculating}
            className="flex-shrink-0"
          >
            {isRecalculating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Calculator className="w-4 h-4" />
            )}
            Recalculer les couts
          </Button>
        )}

        {/* Reset Button */}
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={onReset}>
            <X className="w-4 h-4" />
            Reinitialiser
          </Button>
        )}
      </div>
    </div>
  );
}
